/** EDM settings — the document-control configuration, where it belongs.
 *
 * Four things were project-config keys with no screen: the revision series, the
 * deemed-receipt period, who may close out a review, and the chasing ladder. They are
 * CONTRACTUAL settings, not preferences, so each one is stated with what it does rather
 * than labelled and left to be guessed at.
 *
 * The vendor review-code editor lives here too, and deliberately never offers a mapping
 * onto the project's internal status codes — Code 2 and B1 are different instruments and
 * nobody agreed they are equivalent.
 */
import { useCallback, useEffect, useState } from 'react';
import { HardDrive, Loader2, Plus, Save, Trash2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import { Btn, Field, Input, Panel, Select, Stat } from '@/components/nfa/NfaPrimitives';

const gb = (n) => (n == null ? '—' : `${(n / 1024 ** 3).toFixed(2)} GB`);

const Check = ({ T, checked, onChange, label, testId }) => (
  <label className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer"
    style={{ color: T.body }}>
    <input type="checkbox" checked={!!checked} data-testid={testId}
      onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

const EdmSettings = ({ projectId }) => {
  const T = useNfaTheme();
  const [meta, setMeta] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    if (!projectId) return;
    api.get(`/edm/meta?project_id=${projectId}`).then((r) => {
      setMeta(r.data);
      setCfg({
        revision_scheme: r.data.revisionScheme?.key,
        deemed_receipt_days: r.data.config?.deemed_receipt_days ?? '',
        close_out_roles: r.data.closeOutAuthority?.rolesAllowed || [],
        ...Object.fromEntries(Object.entries(r.data.chasing || {})
          .map(([k, v]) => [`chasing_${k}`, v])),
      });
    }).catch(() => {});
    api.get(`/edm/projects/${projectId}/vdr/review-codes`)
      .then((r) => setCodes(r.data.codes || [])).catch(() => {});
  }, [projectId]);
  useEffect(load, [load]);

  const save = async (patch, what) => {
    setBusy(what);
    try {
      await api.put(`/edm/projects/${projectId}/config`, patch);
      toast.success(`${what} saved`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.error || `Could not save ${what}`));
    } finally { setBusy(null); }
  };

  const saveCodes = async () => {
    setBusy('review codes');
    try {
      await api.put(`/edm/projects/${projectId}/vdr/review-codes`, { codes });
      toast.success('Vendor review codes saved');
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d
        : `${d?.error || 'Refused'}${d?.codes ? `: ${d.codes.join(', ')}` : ''}`);
    } finally { setBusy(null); }
  };

  if (!meta || !cfg) {
    return <div className="py-10 text-center text-[11.5px]" style={{ color: T.muted }}
      data-testid="edm-settings-loading">
      <Loader2 className="w-4 h-4 animate-spin inline" /> Loading configuration…
    </div>;
  }

  const may = meta.closeOutAuthority?.allowed;
  const usage = meta.usage || {};
  const store = meta.storage || {};
  const setCh = (k, v) => setCfg((c) => ({ ...c, [`chasing_${k}`]: v }));

  return (
    <div className="space-y-3" data-testid="edm-settings">
      {!may && (
        <div className="rounded-lg px-3 py-2.5 text-[11px]" data-testid="edm-settings-readonly"
          style={{ background: T.tint(T.amber), border: `1px solid ${T.tint(T.amber, '44')}`,
            color: T.ink }}>
          You are reading this configuration, not editing it. {meta.closeOutAuthority?.reason}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat T={T} label="Revision series" value={meta.revisionScheme?.key || '—'}
          sub={meta.revisionScheme?.label} testId="edm-set-stat-scheme"
          info="The revision series this project issues in. Changing it never rewrites
                revisions already issued — the next revision simply continues in the new
                series." />
        <Stat T={T} label="Deemed receipt" value={meta.config?.deemed_receipt_days ?? 'off'}
          sub={meta.config?.deemed_receipt_days ? 'days after delivery' : 'acknowledgement only'}
          testId="edm-set-stat-deemed"
          info="A CONTRACTUAL period: a transmittal counts as received this many days after
                delivery unless the receiving party acknowledges earlier or objects. Set it
                from the contract, never from preference." />
        <Stat T={T} label="Storage" value={store.backend || '—'}
          sub={store.durableAcrossDeploy ? 'survives redeployment' : 'EPHEMERAL'}
          tone={store.durableAcrossDeploy ? undefined : T.red}
          testId="edm-set-stat-storage"
          info="Where the bytes live. Anything not durable across a deployment loses every
                uploaded drawing on the next deploy while the database keeps pointing at
                it — the worst kind of failure, because it is silent." />
        <Stat T={T} label="Used" value={gb(usage.usedBytes)}
          sub={`${usage.pct ?? 0}% of ${gb(usage.quotaBytes)}`}
          tone={usage.warn ? T.amber : undefined} testId="edm-set-stat-usage"
          info="Distinct content counted ONCE — identical files shared between revisions are
                stored a single time, so this is smaller than the sum of the file sizes." />
      </div>

      <Panel T={T} testId="edm-settings-series" title="Revision series and status codes"
        subtitle="The series and the status-code set are paired. Shipping ISO suitability codes against a Rev A/B/C series leaves a project half-configured.">
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <Field T={T} label="Revision scheme"
            hint={meta.revisionSchemes?.find((s) => s.key === cfg.revision_scheme)?.example}>
            <Select T={T} value={cfg.revision_scheme} disabled={!may}
              data-testid="edm-set-scheme"
              onChange={(e) => setCfg({ ...cfg, revision_scheme: e.target.value })}>
              {(meta.revisionSchemes || []).map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </Select>
          </Field>
          <div className="text-[10.5px]" style={{ color: T.muted }}>
            Status codes follow the scheme: {(meta.statusCodes || [])
              .map((c) => `${c.code} ${c.label}`).join(' · ') || '—'}
          </div>
          <Btn T={T} disabled={!may || busy} testId="edm-set-save-scheme"
            onClick={() => save({ revision_scheme: cfg.revision_scheme }, 'Revision series')}>
            {busy === 'Revision series' ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Save className="w-3.5 h-3.5" />} Save series
          </Btn>
        </div>
      </Panel>

      <Panel T={T} testId="edm-settings-receipt" title="Deemed receipt and close-out authority"
        subtitle="Two settings that decide when a revision becomes current for construction, and who may stamp it.">
        <div className="grid md:grid-cols-3 gap-3 items-start">
          <Field T={T} label="Deemed receipt (days)"
            hint="Blank = disabled: only an explicit acknowledgement makes a revision current for construction.">
            <Input T={T} type="number" min={0} max={90} disabled={!may}
              data-testid="edm-set-deemed"
              value={cfg.deemed_receipt_days}
              onChange={(e) => setCfg({ ...cfg, deemed_receipt_days: e.target.value })} />
          </Field>
          <Field T={T} label="Who may close out a review"
            hint="At least one role, or no revision could ever be stamped.">
            <div className="flex flex-col gap-1">
              {(meta.closeOutRoles || []).map((r) => (
                <Check key={r} T={T} label={r.replace(/_/g, ' ').toLowerCase()}
                  testId={`edm-set-role-${r}`}
                  checked={cfg.close_out_roles.includes(r)}
                  onChange={(on) => setCfg({
                    ...cfg,
                    close_out_roles: on ? [...cfg.close_out_roles, r]
                      : cfg.close_out_roles.filter((x) => x !== r),
                  })} />
              ))}
            </div>
          </Field>
          <div className="pt-4">
            <Btn T={T} disabled={!may || busy} testId="edm-set-save-receipt"
              onClick={() => save({
                deemed_receipt_days: cfg.deemed_receipt_days === ''
                  ? null : Number(cfg.deemed_receipt_days),
                close_out_roles: cfg.close_out_roles,
              }, 'Receipt and authority')}>
              {busy === 'Receipt and authority' ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Save className="w-3.5 h-3.5" />} Save
            </Btn>
            <p className="text-[10px] mt-2" style={{ color: T.muted }}>
              {meta.config?.deemedReceiptNote}
            </p>
          </div>
        </div>
      </Panel>

      <Panel T={T} testId="edm-settings-chasing" title="The chasing ladder"
        subtitle="Reminders are rate-limited and finite on purpose: auto-emailing a vendor's manager because their engineer took four days is how a document-control system gets switched off.">
        <div className="grid md:grid-cols-5 gap-3 items-end">
          <Field T={T} label="First reminder after (days)" hint="counted from delivery">
            <Input T={T} type="number" min={0} max={60} disabled={!may}
              data-testid="edm-set-chase-after" value={cfg.chasing_reminder_after_days ?? 3}
              onChange={(e) => setCh('reminder_after_days', Number(e.target.value))} />
          </Field>
          <Field T={T} label="Then every (days)">
            <Input T={T} type="number" min={1} max={60} disabled={!may}
              data-testid="edm-set-chase-interval"
              value={cfg.chasing_reminder_interval_days ?? 7}
              onChange={(e) => setCh('reminder_interval_days', Number(e.target.value))} />
          </Field>
          <Field T={T} label="Escalate internally after (days)"
            hint="the human chases; nothing leaves the system">
            <Input T={T} type="number" min={0} max={180} disabled={!may}
              data-testid="edm-set-chase-escalate"
              value={cfg.chasing_escalate_after_days ?? 14}
              onChange={(e) => setCh('escalate_after_days', Number(e.target.value))} />
          </Field>
          <Field T={T} label="Maximum reminders" hint="nobody is chased for ever">
            <Input T={T} type="number" min={0} max={20} disabled={!may}
              data-testid="edm-set-chase-max" value={cfg.chasing_max_reminders ?? 4}
              onChange={(e) => setCh('max_reminders', Number(e.target.value))} />
          </Field>
          <div>
            <Check T={T} testId="edm-set-chase-external"
              checked={cfg.chasing_external_escalation}
              onChange={(on) => setCh('external_escalation', on)}
              label="External escalation contact" />
            <div className="mt-2">
              <Btn T={T} disabled={!may || busy} testId="edm-set-save-chasing"
                onClick={() => save(Object.fromEntries(
                  Object.entries(cfg).filter(([k]) => k.startsWith('chasing_'))),
                  'Chasing ladder')}>
                {busy === 'Chasing ladder' ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Save className="w-3.5 h-3.5" />} Save ladder
              </Btn>
            </div>
          </div>
        </div>
      </Panel>

      <Panel T={T} testId="edm-settings-vdr-codes" title="Vendor review codes"
        subtitle="From the purchase order. Configured independently of the project's internal revision status codes and NEVER mapped onto them."
        right={<>
          <Btn T={T} variant="ghost" disabled={!may} testId="edm-set-code-add"
            onClick={() => setCodes([...(codes || []), {
              code: '', label: '', meaning: '', counts_as_approved: false,
              permits_construction: false, requires_resubmission: false }])}>
            <Plus className="w-3.5 h-3.5" /> Add code
          </Btn>
          <Btn T={T} disabled={!may || busy} testId="edm-set-codes-save" onClick={saveCodes}>
            {busy === 'review codes' ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Save className="w-3.5 h-3.5" />} Save codes
          </Btn>
        </>}>
        {!codes && <div className="text-[11px]" style={{ color: T.muted }}>Loading…</div>}
        <div className="space-y-2">
          {(codes || []).map((c, i) => (
            <div key={i} className="grid md:grid-cols-12 gap-2 items-start rounded-md p-2"
              data-testid={`edm-set-code-${i}`}
              style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
              <div className="md:col-span-1">
                <Input T={T} value={c.code} disabled={!may} placeholder="1"
                  data-testid={`edm-set-code-value-${i}`}
                  onChange={(e) => setCodes(codes.map((x, j) => j === i
                    ? { ...x, code: e.target.value } : x))} />
              </div>
              <div className="md:col-span-3">
                <Input T={T} value={c.label} disabled={!may} placeholder="Approved"
                  data-testid={`edm-set-code-label-${i}`}
                  onChange={(e) => setCodes(codes.map((x, j) => j === i
                    ? { ...x, label: e.target.value } : x))} />
              </div>
              <div className="md:col-span-5">
                <Input T={T} value={c.meaning} disabled={!may}
                  placeholder="What the vendor is obliged to do"
                  data-testid={`edm-set-code-meaning-${i}`}
                  onChange={(e) => setCodes(codes.map((x, j) => j === i
                    ? { ...x, meaning: e.target.value } : x))} />
              </div>
              <div className="md:col-span-2 flex flex-col gap-0.5">
                {[['counts_as_approved', 'approved'],
                  ['permits_construction', 'permits construction'],
                  ['requires_resubmission', 'resubmission required']].map(([k, label]) => (
                  <Check key={k} T={T} label={label} checked={c[k]}
                    testId={`edm-set-code-${k}-${i}`}
                    onChange={(on) => setCodes(codes.map((x, j) => j === i
                      ? { ...x, [k]: on } : x))} />
                ))}
              </div>
              <div className="md:col-span-1 text-right">
                <button type="button" disabled={!may} title="Remove"
                  data-testid={`edm-set-code-remove-${i}`}
                  onClick={() => setCodes(codes.filter((_, j) => j !== i))}
                  className="p-1 rounded hover:opacity-70">
                  <Trash2 className="w-3.5 h-3.5" style={{ color: T.red }} />
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] mt-2" style={{ color: T.muted }}>
          A code that vendor returns already carry cannot be removed — those returns were made
          under it, and rewriting the vocabulary underneath them would silently change what was
          decided. Relabel it instead.
        </p>
      </Panel>

      <SubmittalRetirement T={T} projectId={projectId} />

      <Panel T={T} testId="edm-settings-storage" title="Storage durability"
        subtitle={store.detail}>
        <div className="flex items-start gap-3 text-[11px]" style={{ color: T.body }}>
          <HardDrive className="w-4 h-4 shrink-0" style={{ color: T.muted }} />
          <div>
            <div>
              {store.backend} · {Math.round((store.chunkBytes || 0) / 1024 / 1024)} MB chunks ·
              per-file limit {gb(store.perFileLimitBytes)} · project quota{' '}
              {gb(store.perProjectQuotaBytes)} · free {gb(store.freeBytes)}
            </div>
            {store.warning && (
              <div className="mt-1 font-semibold" style={{ color: T.red }}
                data-testid="edm-set-storage-warning">{store.warning}</div>
            )}
            <div className="mt-1" style={{ color: T.muted }}>{usage.note}</div>
          </div>
        </div>
      </Panel>
    </div>
  );
};

const SubmittalRetirement = ({ T, projectId }) => {
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/edm/projects/${projectId}/submittals/migration-plan`)
      .then((r) => setPlan(r.data)).catch(() => setPlan(null));
  }, [projectId]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/edm/projects/${projectId}/submittals/migrate`);
      setResult(r.data);
      toast.success(`${r.data.created} created, ${r.data.updated} updated`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'The migration failed'); }
    setBusy(false);
  };

  if (!plan) return null;
  return (
    <Panel T={T} testId="edm-settings-submittal-retirement"
      title="Legacy submittals → Vendor Document Register"
      subtitle={plan.note}
      right={<Btn T={T} onClick={run} disabled={busy || !plan.submittals}
        testId="edm-migrate-submittals">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Truck className="w-3 h-3" />}
        Migrate {plan.submittals || 0}
      </Btn>}>
      <div className="text-[11.5px] space-y-2" style={{ color: T.body }}>
        <div>
          {plan.submittals} legacy submittal(s) on this project,{' '}
          {plan.alreadyMigrated} already migrated, {plan.withoutAPurchaseOrder} with no
          purchase-order reference. Re-running updates rather than duplicating.
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.08em] mb-1"
            style={{ color: T.muted }}>What it will NOT invent</div>
          <ul className="space-y-0.5" style={{ color: T.amber }}>
            {plan.refusals.map((r) => <li key={r}>· {r}</li>)}
          </ul>
        </div>
        {result && (
          <div className="rounded p-2" data-testid="edm-migrate-submittals-result"
            style={{ background: T.surfaceAlt }}>{result.statement}</div>
        )}
      </div>
    </Panel>
  );
};

export default EdmSettings;
